import type { Customer } from '@/data/types';
import { deriveNextInspection } from '@/lib/customer-dates';

export interface ReminderItem {
  customerId: string;
  customerName: string;
  date: string; // ISO yyyy-mm-dd
  daysUntil: number; // negative = overdue
  overdue: boolean;
  // Enough detail to plan the trip from the reminder itself: where the site is
  // and how much equipment is there (client #72).
  siteAddress: string;
  extinguisherCount: number;
  hoseCount: number;
}

const DAY = 86_400_000;

// Customers whose next inspection falls within `windowDays` (or is already
// overdue), soonest first. Drives the Reminders page and the sidebar badge.
export function reminderItems(
  customers: Customer[],
  windowDays = 30,
  now: Date = new Date(),
  intervalYears?: number
): ReminderItem[] {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const items: ReminderItem[] = [];
  for (const c of customers) {
    const d = deriveNextInspection(c, intervalYears);
    if (!d) continue;
    const parts = d.split('-');
    if (parts.length !== 3) continue;
    const due = Date.UTC(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2])
    );
    if (Number.isNaN(due)) continue;
    const daysUntil = Math.round((due - today) / DAY);
    if (daysUntil > windowDays) continue;
    const a = c.objectAddress;
    items.push({
      customerId: c.id,
      customerName: c.name,
      date: d,
      daysUntil,
      overdue: daysUntil < 0,
      siteAddress: a
        ? [a.street, [a.zip, a.city].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ')
        : '',
      extinguisherCount: c.extinguishers.length,
      hoseCount: c.hoses.length,
    });
  }
  // Overdue section first, then due-soon; within each, nearest to today first
  // (recently-lapsed above ancient stale, soonest-due above later). Tie-break
  // on date for stable ordering.
  items.sort(
    (a, b) =>
      Number(a.overdue === false) - Number(b.overdue === false) ||
      Math.abs(a.daysUntil) - Math.abs(b.daysUntil) ||
      a.date.localeCompare(b.date)
  );
  return items;
}

export function reminderCount(
  customers: Customer[],
  windowDays = 30,
  now?: Date,
  intervalYears?: number
): number {
  return reminderItems(customers, windowDays, now, intervalYears).length;
}
